import { Download } from '@mui/icons-material';
import { Alert, Box, Button, Chip, Grid, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export const MemberImportDownloadButton = () => {
  const { t } = useTranslation(['member']);

  const simpleColumns = [
    { name: 'Medlemsnr', desc: t('member:importMember.templateCol.memberNr'), required: true },
    { name: 'Förnamn', desc: t('member:importMember.templateCol.firstName'), required: true },
    { name: 'Efternamn', desc: t('member:importMember.templateCol.lastName'), required: true },
    { name: 'Adress', desc: t('member:importMember.templateCol.address'), required: true },
    { name: 'Postnummer', desc: t('member:importMember.templateCol.zipcode'), required: true },
    { name: 'Postort', desc: t('member:importMember.templateCol.city'), required: true },
    { name: 'Födelsedatum', desc: t('member:importMember.templateCol.birthdate') },
    { name: 'Telefon', desc: t('member:importMember.templateCol.phone') },
    { name: 'Epost', desc: t('member:importMember.templateCol.email') },
    { name: 'Inträdesdatum', desc: t('member:importMember.templateCol.admissionDate'), required: true },
    { name: 'Förfallodatum', desc: t('member:importMember.templateCol.expireDate'), required: true },
    { name: 'Medlemskategori', desc: t('member:importMember.templateCol.memberCategory') },
  ];

  const advancedColumns = [
    { name: 'memberNr', desc: t('member:importMember.templateCol.memberNr'), required: true },
    { name: 'firstName', desc: t('member:importMember.templateCol.firstName'), required: true },
    { name: 'lastName', desc: t('member:importMember.templateCol.lastName'), required: true },
    { name: 'COAddress', desc: t('member:importMember.templateCol.coAddress') },
    { name: 'address', desc: t('member:importMember.templateCol.address'), required: true },
    { name: 'zipcode', desc: t('member:importMember.templateCol.zipcode'), required: true },
    { name: 'city', desc: t('member:importMember.templateCol.city'), required: true },
    { name: 'country', desc: t('member:importMember.templateCol.country') },
    { name: 'gender', desc: t('member:importMember.templateCol.gender') },
    { name: 'birthdate', desc: t('member:importMember.templateCol.birthdate') },
    { name: 'phone', desc: t('member:importMember.templateCol.phone') },
    { name: 'phoneOther', desc: t('member:importMember.templateCol.phoneOther') },
    { name: 'email', desc: t('member:importMember.templateCol.email') },
    { name: 'admissionDate', desc: t('member:importMember.templateCol.admissionDate'), required: true },
    { name: 'lastPayedDate', desc: t('member:importMember.templateCol.lastPayedDate') },
    { name: 'expireDate', desc: t('member:importMember.templateCol.expireDate'), required: true },
    { name: 'comment', desc: t('member:importMember.templateCol.comment') },
    { name: 'note', desc: t('member:importMember.templateCol.note') },
    { name: 'custom1–10', desc: t('member:importMember.templateCol.custom') },
    { name: 'newspaper', desc: t('member:importMember.templateCol.newspaper') },
    { name: 'familyHeadNr', desc: t('member:importMember.templateCol.familyHeadNr') },
    { name: 'profiles', desc: t('member:importMember.templateCol.profiles') },
  ];

  type ColumnDef = { name: string; desc: string; required?: boolean };

  const renderColumnList = (columns: ColumnDef[]) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
      {columns.map((col) => (
        <Tooltip key={col.name} describeChild title={col.desc} arrow>
          <Chip label={col.required ? `${col.name} *` : col.name} size='small' variant='outlined' sx={{ cursor: 'help' }} />
        </Tooltip>
      ))}
    </Box>
  );

  const renderTemplateCard = (title: string, desc: string, href: string, columns: ColumnDef[]) => (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Typography variant='subtitle2' gutterBottom>
        {title}
      </Typography>
      <Typography variant='caption' color='text.secondary'>
        {desc}
      </Typography>
      {renderColumnList(columns)}
      <Box sx={{ mt: 'auto', pt: 2 }}>
        <Button component={Link as any} to={href} variant='outlined' size='small' download target='_blank' startIcon={<Download />} fullWidth>
          {t('member:importMember.downloadFile')}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box>
      <Alert severity='info' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {t('member:importMember.introText')}
      </Alert>

      <Box sx={{ p: 2 }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
          1. {t('member:importMember.sectionDownload')}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {renderTemplateCard(
              t('member:importMember.templateSimple'),
              t('member:importMember.templateSimpleDesc'),
              '/importera_medlem_mall_enkel.xlsx',
              simpleColumns,
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderTemplateCard(
              t('member:importMember.templateAdvanced'),
              t('member:importMember.templateAdvancedDesc'),
              '/importera_medlem_mall.xlsx',
              advancedColumns,
            )}
          </Grid>
        </Grid>

        <Typography variant='caption' color='text.secondary' sx={{ mt: 1.5, display: 'block' }}>
          {t('member:importMember.templateHint')}
        </Typography>
      </Box>
    </Box>
  );
};
